import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { Chart, dayLabel } from '~/shared/ui'
import { useZone } from '~/team_tools/components/timezone'

type Series = Record<string, number | null>

/** A history answer re-keyed by calendar day, so series can be matched up. */
const byDay = (history: Record<string, any> = {}, pick = (v: any) => v): Series =>
    Object.fromEntries(Object.entries(history).map(([key, value]) => [dayLabel(key), pick(value)]))

export const AccountsCountChart = ({ days = 14 }) => {
    const [zone] = useZone()
    const [registered, setRegistered] = useState<Series>({})
    const [activated, setActivated] = useState<Series>({})
    const [downloads, setDownloads] = useState<Series>({})
    const [activePlayers, setActivePlayers] = useState<Series>({})

    // All three calls name the same zone, and the series are joined by DAY
    // rather than by array position. Downloads and active players used to be
    // UTC days paired by index with the device's days, so each of their points
    // was drawn beside the wrong day's registrations.
    useEffect(() => {
        jsonrpc('statistic.accounts.count.history', { days, tz: zone }).then((res) => {
            setRegistered(byDay(res.result, ([v]) => v))
            setActivated(byDay(res.result, ([, a]) => a))
        })
        jsonrpc('gameapi.statistic.downloads', { tz: zone }).then((resp) => setDownloads(byDay(resp.result?.history)))
        jsonrpc('gameapi.statistic.active_players', { tz: zone }).then((resp) =>
            setActivePlayers(byDay(resp.result?.history))
        )
    }, [days, zone])

    const labels = Object.keys(registered).sort()
    const on = (series: Series) => labels.map((day) => series[day] ?? null)

    return (
        <Chart.TimeSeries
            data={{
                labels,
                datasets: [
                    {
                        label: 'registrations',
                        data: on(registered),
                        borderColor: '#fff',
                        backgroundColor: '#fff',
                    },
                    {
                        // Not activations per day: no activation timestamp is
                        // stored. This is how many of that day's signups have
                        // confirmed their email since, so a confirmation today
                        // moves the day they signed up, not today.
                        label: 'of which activated (by signup day)',
                        data: on(activated),
                        borderColor: 'rgb(34, 197, 94)',
                        backgroundColor: 'rgb(34, 197, 94)',
                    },
                    {
                        label: 'downloads',
                        data: on(downloads),
                        borderColor: '#eab308',
                        backgroundColor: '#eab308',
                    },
                    {
                        label: 'active players',
                        data: on(activePlayers),
                        borderColor: '#f43f5e',
                        backgroundColor: '#f43f5e',
                    },
                ],
            }}
        />
    )
}
